import {
  Button,
  Col,
  Form,
  Input,
  message,
  Row,
  Select,
  Upload,
  Modal,
  Space,
} from "antd";
import React, { useEffect, useState, useCallback } from "react";
import Barcode from "react-barcode";
import { useNavigate } from "react-router-dom";
import API from "../../api";
import {
  DeleteOutlined,
  PlusCircleFilled,
  PlusOutlined,
  PlusSquareFilled,
  PlusSquareOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import { useSelector } from "react-redux";

const { Option } = Select;

const ProductForm = () => {
  const [form] = Form.useForm();
  const [brand, setBrand] = useState([]);
  const [Offer, setOffer] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectConditions, setSelectedConditions] = useState([]);
  const [selectedRefundType, setSelectedRefundType] = useState([]);
  const [finalPrice, setFinalPrice] = useState(0);
  const [fields, setFields] = useState([]);
  const [barcode, setBarcode] = useState("");
  const [fileList, setFileList] = useState([]);
  const [image, setImage] = useState(null);
  const [brandModalOpen, setBrandModalOpen] = useState(false);
  const [categoryModalOpen, setCategoryModalOpen] = useState(false);
  const [categoryForm] = Form.useForm();
  const [taxes, setTaxes] = useState([]);
  const [selectedTax, setSelectedTax] = useState();
  const [user, setUser] = useState(null);
  const [selectedStoreId, setSelectedStoreId] = useState(null);
  const [storeSettings, setStoreSettings] = useState({
    trackingNumber: "No",
    batchNumber: "No",
  });

  const productSettings = useSelector(
    (state) => state.productSetting?.productSetting
  );

  const navigate = useNavigate();

  const fetchUser = useCallback(() => {
    const userData = JSON.parse(localStorage?.getItem("userData"));
    setUser(userData);

    if (userData) {
      if (userData.role === "admin") {
        const settings = productSettings.find(
          (setting) => setting.storeId === selectedStoreId
        );
        if (settings) {
          setStoreSettings({
            trackingNumber: settings?.engineNumber || "No",
            batchNumber: settings?.batchNumber || "No",
          });
        } else {
          setStoreSettings({ trackingNumber: "No", batchNumber: "No" });
        }
      } else if (userData.storeId) {
        const settings = productSettings.find(
          (setting) => setting.storeId === userData.storeId
        );

        if (settings) {
          setStoreSettings({
            trackingNumber: settings?.engineNumber || "No",
            batchNumber: settings?.batchNumber || "No",
          });
        }
      }
    }
  }, [selectedStoreId, productSettings]);

  const handleAddField = () => {
    setFields([...fields, { key: "", value: "" }]);
  };

  const handleRemoveField = (index) => {
    const newFields = fields.filter((_, i) => i !== index);
    setFields(newFields);
  };

  const handleInputChange = (index, fieldName, value) => {
    const updatedFields = [...fields];
    updatedFields[index][fieldName] = value;
    setFields(updatedFields);
  };

  const fetchBrand = async () => {
    try {
      const response = await API.get("/brands");
      setBrand(response?.data || []);
    } catch (error) {
      console.error("Failed to fetch brand:", error);
      message.error(error.response?.data?.message);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await API.get("/category");
      setCategories(response?.data || []);
    } catch (error) {
      console.error("Failed to fetch categories:", error);
      message.error("Unable to load categories.");
    }
  };

  const fetchTax = async () => {
    try {
      const taxResponse = await API.get("/taxes");
      setTaxes(taxResponse?.data);
    } catch (error) {
      console.error("Error fetching data:", error);
      message.error("Failed to fetch data.");
    }
  };

  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      const detailsObject = fields.reduce((acc, field) => {
        if (field.key && field.value) {
          acc[field.key] = field.value;
        }
        return acc;
      }, {});
      const payload = {
        ...values,
        specifications: { ...values?.specifications, details: detailsObject },
        pricing: {
          ...values?.pricing,
          taxes: [
            {
              taxId: selectedTax?._id,
              taxName: selectedTax?.taxName,
              percentage: selectedTax?.percentage,
            },
          ],
        },
        image: image,
        trackingType:
          storeSettings.trackingNumber === "Yes"
            ? values.trackingType
            : undefined,
      };

      const res = await API.post("/products", payload);

      if (res.status === 200) {
        message.success("Product added successfully!");
        form.resetFields();
        setFileList([]);
      }
    } catch (error) {
      console.error("Submit failed:", error);
      message.error(error.response?.data?.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories();
    fetchBrand();
    fetchTax();
    fetchUser();
    form.resetFields();
  }, [fetchUser]);

  const handleChange = async (info) => {
    let updatedFileList = [...info.fileList];
    updatedFileList = updatedFileList?.filter(
      (file) => file?.status !== "error"
    );

    if (info.file.status === "done") {
      message.success(`${info.file.name} file uploaded successfully`);
      const imagePath = info?.file?.response?.imagePath;
      setImage(imagePath);
      setFileList([info?.file]);
    } else if (info.file.status === "error") {
      message.error(`${info?.file?.name} file upload failed.`);
    }
    setFileList(updatedFileList);
  };

  const customRequest = async (options) => {
    const { file, onSuccess, onError } = options;
    const userId = JSON.parse(localStorage.getItem("userData"))?.id;

    const formData = new FormData();
    formData.append("previousImagePath", image);
    formData.append("userId", userId);
    formData.append("image", file);

    try {
      const response = await API.post("/products/upload", formData);
      message.success("Image uploaded successfully!");
      setImage(response?.data?.imagePath);

      onSuccess(response?.data);
      setFileList([
        {
          uid: file?.uid,
          name: file?.name,
          status: "done",
          url: `http://localhost:5001/${response?.data?.imagePath}`,
        },
      ]);
    } catch (error) {
      console.error("Error uploading image:", error);
      onError(error);
      message.error("Failed to upload image.");
    }
  };

  const handleRemoveImage = async (file) => {
    const productId = JSON.parse(localStorage.getItem("userData"))?.id;
    const fileUrl = file?.url;
    const fileName = fileUrl.split("/").pop();

    try {
      const response = await API.delete(`/products/image/${fileName}`, {
        data: { imagePath: file?.url },
      });

      if (response.status === 200) {
        message.success("Image removed successfully");
        setImage(null);
        setFileList([]);
      }
    } catch (error) {
      console.error("Error removing image:", error);
      message.error("Failed to remove image");
    }
  };

  const handleCondition = (value) => {
    setSelectedConditions(value);
  };

  const handleRefundType = (value) => {
    setSelectedRefundType(value);
  };

  const handleAddBrand = async (newBrand) => {
    try {
      const response = await API.post("/brands", newBrand);
      message.success("Brand added successfully!");
      setBrandModalOpen(false);
      fetchBrand();
    } catch (err) {
      console.error(err);
      message.error(err.response?.data?.message);
    }
  };

  const handleAddCategory = async (values) => {
    try {
      const response = await API.post(`/category`, values);
      if (response.status === 200) {
        message.success("Category added successfully!");
        setCategoryModalOpen(false);
        fetchCategories();
      }
    } catch (err) {
      console.error(err);
      message.error(err.response?.data?.message);
    }
  };

  return (
    <div
      style={{
        padding: "20px",
        background: "#fff",
        borderRadius: "8px",
        boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
      }}
    >
      <Button
        type="primary"
        style={{ margin: "10px -8px" }}
        onClick={() => navigate("/products")}
      >
        Show Products
      </Button>

      <Form form={form} layout="vertical" onFinish={handleSubmit}>
        <Row
          gutter={16}
          style={{
            minHeight: 360,
            background: "white",
            borderRadius: "5px",
            padding: "10px",
          }}
        >
          {/* Brand Field */}
          <Col xs={18} sm={7} lg={7}>
            <Form.Item
              label="Brand"
              name="brand"
              rules={[{ required: true, message: "Please select a brand!" }]}
            >
              <Select
                placeholder="Select brand"
                showSearch
                optionFilterProp="children"
                filterOption={(input, option) => {
                  const label =
                    typeof option?.children === "string" ? option.children : "";
                  return label.toLowerCase().includes(input.toLowerCase());
                }}
              >
                {brand.map((brand) => (
                  <Option key={brand._id} value={brand._id}>
                    {brand.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col xs={3} sm={2} lg={1}>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => setBrandModalOpen(true)}
              style={{ marginTop: "30px" }}
            />
          </Col>

          {/* Category Field */}
          <Col xs={18} sm={7} lg={7}>
            <Form.Item
              label="Category"
              name="category"
              rules={[{ required: true, message: "Please select a category!" }]}
            >
              <Select
                placeholder="Select category"
                showSearch
                optionFilterProp="children"
                filterOption={(input, option) => {
                  const label =
                    typeof option?.children === "string" ? option.children : "";
                  return label.toLowerCase().includes(input.toLowerCase());
                }}
              >
                {categories.map((category) => (
                  <Option key={category._id} value={category._id}>
                    {category.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col xs={3} sm={2} lg={1}>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => setCategoryModalOpen(true)}
              style={{ marginTop: "30px" }}
            />
          </Col>

          {/* Product Name Field */}
          <Col xs={8} sm={8} lg={8}>
            <Form.Item
              label="Product Name"
              name={["specifications", "name"]}
              rules={[
                {
                  required: true,
                  message: "Please enter the Name of the product!",
                },
              ]}
            >
              <Input placeholder="Enter Name" />
            </Form.Item>
          </Col>

          {/* HSN ASC Field */}
          <Col xs={24} sm={6} lg={6}>
            <Form.Item
              label="HSN ASC"
              name="hsn_sac"
              rules={[{ required: true, message: "Please enter HSN ASC!" }]}
            >
              <Input placeholder="Enter HSN ASC" />
            </Form.Item>
          </Col>

          <Col xs={24} sm={6} lg={6}>
            <Form.Item
              label="Product Type"
              name="type"
              rules={[{ required: true, message: "Select Product Type!" }]}
            >
              <Select placeholder="Select Product Type">
                <Option value="parts">Parts</Option>
                <Option value="bike">Bike</Option>
              </Select>
            </Form.Item>
          </Col>

          {/* Pricing Section */}
          <Col xs={24} sm={6} lg={6}>
            <Form.Item
              label="MRP"
              name={["pricing", "mrpPrice"]}
              rules={[{ required: true, message: "Please enter the MRP!" }]}
            >
              <Input type="number" placeholder="0.00" />
            </Form.Item>
          </Col>
          <Col xs={24} sm={6} lg={6}>
            <Form.Item
              label="Sale Price"
              name={["pricing", "salePrice"]}
              rules={[
                { required: true, message: "Please enter the sale price!" },
              ]}
            >
              <Input type="number" placeholder="0.00" />
            </Form.Item>
          </Col>
          <Col xs={24} sm={6} lg={6}>
            <Form.Item
              label="Purchase Price"
              name={["pricing", "purchasePrice"]}
            >
              <Input type="number" placeholder="0.00" />
            </Form.Item>
          </Col>
          <Col xs={24} sm={6} lg={6}>
            <Form.Item label="GST" name="taxes">
              <Select
                placeholder="Select Tax (GST)"
                onChange={(value) => {
                  const selectedTax = taxes.find((tax) => tax._id === value);
                  setSelectedTax(selectedTax);
                }}
              >
                {taxes.map((taxes) => (
                  <Option key={taxes._id} value={taxes._id}>
                    {taxes.percentage} %
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>

          {/* Type Field (Conditional) */}
          {storeSettings.trackingNumber === "Yes" && (
            <Col xs={24} sm={6} lg={4}>
              <Form.Item
                label="Type"
                name="trackingType"
                rules={[
                  { required: true, message: "Please select a tracking type!" },
                ]}
              >
                <Select placeholder="Select Type">
                  <Option value="serialNumber">Serial Number</Option>
                  <Option value="imei">IMEI</Option>
                  <Option value="other">Other</Option>
                </Select>
              </Form.Item>
            </Col>
          )}

          {/* Code Field */}
          <Col xs={24} sm={6} lg={4}>
            <Form.Item label="Code" name="code">
              <Input placeholder="Enter Code" />
            </Form.Item>
          </Col>

          {/* Barcode Field */}
          <Col xs={24} sm={8} lg={8}>
            <Form.Item label="Barcode" name={["barcode", "code"]}>
              <Input
                placeholder="Enter barcode"
                maxLength={30}
                onChange={(e) => [
                  form.setFieldsValue({ "barcode.code": e.target.value }),
                  setBarcode(e.target.value),
                ]}
              />
            </Form.Item>
            <Col xs={24} sm={16} lg={12}>
              <div
                style={{
                  textAlign: "center",
                  marginTop: "24px",
                  width: "150px",
                }}
              >
                {barcode && <Barcode value={barcode} height={50} width={1} />}
              </div>
            </Col>
          </Col>

          <Col xs={24} sm={12}>
            <Form.Item
              label="Upload Product Image"
              rules={[
                {
                  required: true,
                  message: "Please upload at least one image!",
                },
              ]}
            >
              <Upload
                customRequest={customRequest}
                action={null}
                listType="picture"
                fileList={fileList}
                onChange={handleChange}
                onRemove={handleRemoveImage}
                multiple={false}
              >
                <Button type="primary" icon={<UploadOutlined />}>
                  Upload
                </Button>
              </Upload>
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col
            span={24}
            style={{
              display: "flex",
              justifyContent: "end",
              marginTop: "60px",
            }}
          >
            {/* Submit Button */}
            <Button
              type="primary"
              htmlType="submit"
              loading={loading}
              style={{ padding: "20px 50px" }}
            >
              Save Product
            </Button>
          </Col>
        </Row>
      </Form>

      {/* Brand Modal */}
      {brandModalOpen && (
        <Modal
          title="Add Brand"
          visible={brandModalOpen}
          onCancel={() => setBrandModalOpen(false)}
          footer={null}
          width={600}
        >
          <Form layout="vertical" onFinish={handleAddBrand} autoComplete="off">
            <Form.Item
              label="Brand Name"
              name="name"
              rules={[
                { required: true, message: "Please enter the brand name." },
              ]}
            >
              <Input />
            </Form.Item>

            <Form.Item>
              <Button type="primary" block htmlType="submit">
                Add Brand
              </Button>
            </Form.Item>
          </Form>
        </Modal>
      )}

      {/* Category Modal */}
      {categoryModalOpen && (
        <Modal
          title="Add Category"
          visible={categoryModalOpen}
          onCancel={() => setCategoryModalOpen(false)}
          footer={null}
          width={600}
        >
          <Form
            layout="vertical"
            onFinish={handleAddCategory}
            autoComplete="off"
            form={categoryForm}
          >
            <Form.Item
              label="Category Name"
              name="name"
              rules={[
                { required: true, message: "Please enter the category name." },
              ]}
            >
              <Input />
            </Form.Item>

            <Form.Item>
              <Button type="primary" block htmlType="submit">
                Add Category
              </Button>
            </Form.Item>
          </Form>
        </Modal>
      )}
    </div>
  );
};

export default ProductForm;
